import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Package } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import type { OutboxRow } from '@/components/patient/referralOutboxData';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import {
  VANS_TRANSPORTS,
  decidingVansReceipt,
  downloadUtf8Xml,
  vansStatusExpectsReceipt,
  type VansAttempt,
  type VansAttemptsResult,
  type VansTransport,
} from '@/lib/referral/vansEnvelope';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

type PackResult = { id: string; envelopeIdentifier: string; attempt: number; xml: string };
type DocumentResult = { id: string; kind: string; xml: string };

export function vansAttemptsQueryKey(clinicId: string, referralId: string) {
  return ['hospital-referral-vans', clinicId, referralId] as const;
}

/**
 * VANS envelopes for one outbox row (C16–C18): pack the sent message (and each
 * XBIN02 attachment) in a new VANSEnvelope with a chosen Transport/Type, list
 * every attempt with its VANS receipt status, download it, and resend in a new
 * envelope. Loaded only when opened, so the outbox list stays as fast as before.
 */
export function OutboxVansPanel({ clinicId, row }: { clinicId: string; row: OutboxRow }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [transport, setTransport] = useState<VansTransport>('reliable');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const queryKey = vansAttemptsQueryKey(clinicId, row.id);
  const query = useQuery({
    queryKey,
    enabled: open,
    queryFn: () =>
      callFunction<Record<string, unknown>, VansAttemptsResult>('communicate-ctl', {
        action: 'vans-attempts',
        clinicId,
        referralId: row.id,
      }),
  });

  const exchanges = [
    { exchangeId: row.exchange_id, label: row.kind, fileStem: row.kind },
    ...row.companions.map((c) => ({
      exchangeId: c.exchangeId,
      label: `${t('patients.hospital_referral.vans_attachment', 'Bilag')}: ${c.fileName}`,
      fileStem: `XBIN02_${c.fileName.replace(/[\\/]/g, '_')}`,
    })),
  ];

  function failText(err: unknown, fallback: string): string {
    return err instanceof ApiCallError ? err.message : fallback;
  }

  async function pack(exchangeId: string, fileStem: string) {
    setBusy(exchangeId);
    setError('');
    try {
      const result = await callFunction<Record<string, unknown>, PackResult>('communicate-ctl', {
        action: 'vans-pack',
        clinicId,
        referralId: row.id,
        exchangeId,
        transport,
      });
      downloadUtf8Xml(result.xml, `VANS_${fileStem}_${result.envelopeIdentifier}.xml`);
      await queryClient.invalidateQueries({ queryKey });
    } catch (err) {
      setError(failText(err, t('patients.hospital_referral.vans_pack_failed', 'VANSEnvelope kunne ikke dannes.')));
    } finally {
      setBusy(null);
    }
  }

  async function download(id: string, fileName: string) {
    setBusy(id);
    setError('');
    try {
      const result = await callFunction<Record<string, unknown>, DocumentResult>('communicate-ctl', {
        action: 'vans-document',
        clinicId,
        referralId: row.id,
        id,
      });
      downloadUtf8Xml(result.xml, fileName);
    } catch (err) {
      setError(failText(err, t('patients.hospital_referral.vans_download_failed', 'Filen kunne ikke hentes.')));
    } finally {
      setBusy(null);
    }
  }

  function statusLabel(attempt: VansAttempt): string {
    if (attempt.status === 'positive') {
      return t('patients.hospital_referral.vans_status_positive', 'Positiv VANS-kvittering');
    }
    if (attempt.status === 'negative') {
      return t('patients.hospital_referral.vans_status_negative', 'Negativ VANS-kvittering');
    }
    return vansStatusExpectsReceipt(attempt)
      ? t('patients.hospital_referral.vans_status_awaiting', 'Afventer VANS-kvittering')
      : t('patients.hospital_referral.vans_status_no_receipt', 'Ingen VANS-kvittering (unreliable)');
  }

  function transportLabel(value: VansTransport): string {
    if (value === 'reliable') return t('patients.hospital_referral.vans_transport_reliable', 'reliable (kvittering ønskes)');
    if (value === 'unreliable') {
      return t('patients.hospital_referral.vans_transport_unreliable', 'unreliable (ingen kvittering)');
    }
    return t('patients.hospital_referral.vans_transport_none', 'Ingen Transport-element');
  }

  return (
    <div data-testid="outbox-vans-panel">
      <button
        type="button"
        className="hv-disc"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Package className="h-5 w-5" aria-hidden />
        <span>
          <b>{t('patients.hospital_referral.vans_title', 'VANSEnvelope')}</b>
          <span>
            {t(
              'patients.hospital_referral.vans_collapsed_hint',
              'Pak i VANSEnvelope, se VANS-kvitteringer og gensend i ny kuvert.',
            )}
          </span>
        </span>
        <ChevronDown className={cn('h-4 w-4', open && 'rotate-180')} aria-hidden />
      </button>
      {open ? (
      <div className="mt-3 space-y-3">
        <p className={FORM_HINT_TYPE}>
          {t(
            'patients.hospital_referral.vans_hint',
            'Hver indpakning får nyt kuvert-ID (EnvelopeIdentifier) og nyt Message Identifier. Gensend ved at pakke igen.',
          )}
        </p>
        <label className="block max-w-xs">
          <span className={FORM_LABEL_TYPE}>{t('patients.hospital_referral.vans_transport', 'Transport/Type')}</span>
          <select
            value={transport}
            onChange={(event) => setTransport(event.target.value as VansTransport)}
            className="mt-1 block w-full rounded-lg border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-1 focus:ring-ds-teal"
          >
            {VANS_TRANSPORTS.map((value) => (
              <option key={value} value={value}>
                {transportLabel(value)}
              </option>
            ))}
          </select>
        </label>
        {error && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {error}
          </p>
        )}
        {query.isLoading && !query.data && (
          <div className="flex flex-col gap-s3" aria-busy>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {query.isError && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {failText(query.error, t('patients.hospital_referral.vans_list_failed', 'VANS-kuverterne kunne ikke hentes.'))}
          </p>
        )}
        {exchanges.map((exchange) => {
          const attempts = query.data?.exchanges.find((e) => e.exchangeId === exchange.exchangeId)?.attempts ?? [];
          return (
            <div key={exchange.exchangeId} className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className={cn(TYPE.body, 'font-medium')}>{exchange.label}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={busy !== null}
                  onClick={() => void pack(exchange.exchangeId, exchange.fileStem)}
                >
                  {attempts.length === 0
                    ? t('patients.hospital_referral.vans_pack', 'Pak i VANSEnvelope')
                    : t('patients.hospital_referral.vans_resend', 'Gensend i ny kuvert')}
                </Button>
              </div>
              {attempts.length > 0 && (
                <ul className="space-y-2">
                  {attempts.map((attempt, index) => {
                    const deciding = decidingVansReceipt(attempt);
                    const negative = attempt.status === 'negative';
                    return (
                      <li
                        key={attempt.id}
                        className={cn(
                          'rounded-md border p-2',
                          negative
                            ? 'border-ds-red bg-ds-red-tint  '
                            : attempt.status === 'positive'
                              ? 'border-ds-green-line bg-ds-green-tint  '
                              : 'border-ds-line ',
                        )}
                      >
                        <p className={TYPE.body}>
                          {t('patients.hospital_referral.vans_attempt', 'Forsøg')} {index + 1} · {statusLabel(attempt)}
                        </p>
                        <p className={cn(FORM_HINT_TYPE, 'break-all')}>
                          EnvelopeIdentifier {attempt.envelopeIdentifier}
                          {attempt.messageIdentifier && <> · Message Identifier {attempt.messageIdentifier}</>}
                          {' · '}Transport/Type {attempt.transport === 'none' ? '–' : attempt.transport}
                          {attempt.sizeInBytes !== null && <> · SizeInBytes {attempt.sizeInBytes}</>}
                          {' · '}{formatReferralDateTime(attempt.createdAt)}
                        </p>
                        {deciding && (deciding.errorCode || deciding.errorText) && (
                          <p className={cn('mt-1 whitespace-pre-wrap', TYPE.body)}>
                            {/* errorText already starts with the code (Communicate's vans-error check). */}
                            {deciding.errorText ?? deciding.errorCode}
                          </p>
                        )}
                        {attempt.receipts.some((r) => !r.matchesSent) && (
                          <p className={cn('mt-1', FORM_ERROR_TYPE)}>
                            {t(
                              'patients.hospital_referral.vans_receipt_mismatch',
                              'Kvitteringens OriginalMessage svarer ikke til den sendte kuvert.',
                            )}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={busy !== null}
                            onClick={() =>
                              void download(attempt.id, `VANS_${exchange.fileStem}_${attempt.envelopeIdentifier}.xml`)
                            }
                          >
                            {t('patients.hospital_referral.vans_download', 'Hent VANSEnvelope')}
                          </Button>
                          {attempt.receipts.map((receipt) => (
                            <Button
                              key={receipt.id}
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busy !== null}
                              onClick={() => void download(receipt.id, `VANS_${receipt.kind}_${attempt.envelopeIdentifier}.xml`)}
                            >
                              {t('patients.hospital_referral.vans_download_receipt', 'Hent kvittering')}: {receipt.kind}
                            </Button>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      ) : null}
    </div>
  );
}
